import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import { Search, ArrowRight, ChevronRight, ChevronLeft, Phone, Scale, Paintbrush, Shield, Handshake } from "lucide-react";
import PropertyCard from "@/components/PropertyCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_real-estate-hub-147/artifacts/bvwh84dv_PHOTO%20ACCUEIL.jpg";

const services = [
  {
    icon: Scale,
    title: "Expertise juridique",
    desc: "Un accompagnement juridique complet par un avocat breveté pour sécuriser chaque étape de votre transaction.",
  },
  {
    icon: Paintbrush,
    title: "Valorisation créative",
    desc: "Une mise en valeur professionnelle de votre bien grâce à notre expertise en communication et arts graphiques.",
  },
  {
    icon: Shield,
    title: "Transparence totale",
    desc: "Une communication claire et honnête, sans surprise. Vous êtes informé à chaque étape du processus.",
  },
  {
    icon: Handshake,
    title: "Service sur mesure",
    desc: "Un suivi personnalisé adapté à vos besoins et à votre projet, du début à la signature.",
  },
];

export default function HomePage() {
  const [properties, setProperties] = useState([]);
  const [news, setNews] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // News carousel
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  // Featured properties carousel
  const [propsEmblaRef, propsEmblaApi] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const propsPrev = useCallback(() => propsEmblaApi && propsEmblaApi.scrollPrev(), [propsEmblaApi]);
  const propsNext = useCallback(() => propsEmblaApi && propsEmblaApi.scrollNext(), [propsEmblaApi]);

  useEffect(() => {
    axios.get(`${API}/properties?featured=true`).then((r) => setProperties(r.data)).catch(() => {});
    axios.get(`${API}/news`).then((r) => setNews(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative h-screen overflow-hidden">
        <img src={HERO_IMAGE} alt="Genève" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/50" />

        {/* Title + Search bar - right 2/3, top 1/3 */}
        <div className="absolute top-[33%] right-[16%] z-10">
          <h1
            className="font-accent italic text-white text-3xl sm:text-4xl lg:text-5xl mb-8 animate-fade-in-up text-left"
          >
            Bienvenue chez vous
          </h1>

          <div
            className="flex flex-col sm:flex-row gap-2 w-[320px] lg:w-[420px] animate-fade-in-up"
            style={{ animationDelay: "200ms" }}
          >
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
              <input
                data-testid="hero-search-input"
                type="text"
                placeholder="Je recherche un bien à..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/90 backdrop-blur-sm pl-10 pr-3 py-3 text-sm text-[#333] placeholder:text-[#999] focus:outline-none"
              />
            </div>
            <Link
              to={searchQuery ? `/biens?search=${encodeURIComponent(searchQuery)}` : "/biens"}
              data-testid="hero-search-btn"
              className="bg-[#699ab6] text-white px-5 py-3 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#5a8ba5] transition-colors flex items-center justify-center gap-2"
            >
              <Search className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Properties - Coup de Cœur */}
      {properties.length > 0 && (
        <section data-testid="featured-properties" className="py-20 lg:py-32 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 lg:mb-16 gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
                  Sélection
                </p>
                <h2 className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight">
                  Nos coups de cœur
                </h2>
              </div>
              <div className="flex items-center gap-4">
                {properties.length > 3 && (
                  <div className="flex gap-2">
                    <button
                      data-testid="featured-prev"
                      onClick={propsPrev}
                      className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center hover:bg-[#699ab6] hover:border-[#699ab6] hover:text-white text-[#777] transition-all"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      data-testid="featured-next"
                      onClick={propsNext}
                      className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center hover:bg-[#699ab6] hover:border-[#699ab6] hover:text-white text-[#777] transition-all"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <Link
                  to="/biens"
                  data-testid="view-all-properties"
                  className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-medium text-[#699ab6] hover:text-[#5a8ba5] transition-colors"
                >
                  Voir tous les biens
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {properties.length <= 3 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                {properties.map((property, i) => (
                  <PropertyCard key={property.id} property={property} index={i} />
                ))}
              </div>
            ) : (
              <div className="embla" ref={propsEmblaRef}>
                <div className="embla__container">
                  {properties.map((property, i) => (
                    <div key={property.id} className="embla__slide px-4" style={{ flex: "0 0 33.333%" }}>
                      <PropertyCard property={property} index={i} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* News Carousel */}
      {news.length > 0 && (
        <section data-testid="news-section" className="bg-[#F5F5F5] py-20 lg:py-32 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 lg:mb-16 gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
                  Actualités
                </p>
                <h2 className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight">
                  Nos dernières nouvelles
                </h2>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <button
                    data-testid="news-prev"
                    onClick={scrollPrev}
                    className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center hover:bg-[#699ab6] hover:border-[#699ab6] hover:text-white text-[#777] transition-all"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="news-next"
                    onClick={scrollNext}
                    className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center hover:bg-[#699ab6] hover:border-[#699ab6] hover:text-white text-[#777] transition-all"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <Link
                  to="/actualites"
                  data-testid="view-all-news"
                  className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-medium text-[#699ab6] hover:text-[#5a8ba5] transition-colors"
                >
                  Voir tous les articles
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="embla" ref={emblaRef}>
              <div className="embla__container">
                {news.map((article) => (
                  <div key={article.id} className="embla__slide px-4">
                    <Link to={`/actualites/${article.id}`} data-testid={`news-card-${article.id}`} className="group block">
                      <div className="overflow-hidden aspect-[16/10] mb-5 bg-[#eee]">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="font-heading text-xl font-medium text-[#333333] mb-2 group-hover:text-[#699ab6] transition-colors">
                        {article.title}
                      </h3>
                      <p className="font-accent italic text-[#699ab6] text-sm mb-3">
                        {article.subtitle}
                      </p>
                      <p className="text-sm text-[#777] font-light leading-relaxed line-clamp-3">
                        {article.content}
                      </p>
                      <span className="inline-block mt-3 text-[11px] uppercase tracking-[0.15em] font-medium text-[#699ab6] group-hover:underline">
                        Lire la suite
                      </span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* About Preview */}
      <section data-testid="about-preview" className="py-20 lg:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_real-estate-hub-147/artifacts/bn659r7w_Teinte_Saturation%201%20copie%202.jpg"
                alt="Notre équipe"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
                L'agence
              </p>
              <h2 className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight mb-8">
                Un savoir-faire
                <br />
                à votre service
              </h2>
              <p className="text-[#777777] text-base leading-relaxed mb-6 font-light">
                Née de l'union entre un juriste, titulaire du brevet d'avocat et d'un spécialiste
                en communication et arts graphiques, ces deux entrepreneurs ont décidé de mettre
                leurs compétences respectives en commun afin de fonder Vingt-Neuf Immobilier.
              </p>
              <p className="text-[#777777] text-base leading-relaxed mb-10 font-light">
                Notre approche personnalisée, notre transparence et notre engagement inébranlable
                garantissent une expérience immobilière sans stress et pleinement satisfaisante.
              </p>
              <Link
                to="/a-propos"
                data-testid="about-link"
                className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.15em] font-medium text-[#699ab6] border-b border-[#699ab6] pb-1 hover:text-[#5a8ba5] hover:border-[#5a8ba5] transition-colors"
              >
                Découvrir l'agence
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="py-12 lg:py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
              Notre engagement
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight">
              Pourquoi nous choisir
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <div key={i} className="text-center group">
                  <div className="w-14 h-14 mx-auto mb-5 bg-[#F5F5F5] flex items-center justify-center group-hover:bg-[#699ab6] transition-colors duration-500">
                    <Icon className="w-6 h-6 text-[#699ab6] group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="font-heading text-lg font-medium text-[#333333] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#777777] font-light leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F5F5F5] py-20 lg:py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-accent italic text-[#699ab6] text-lg mb-4">
            Prêt à concrétiser votre projet ?
          </p>
          <h2 className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight mb-8">
            Parlons de votre projet immobilier
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="bg-[#699ab6] text-white px-10 py-4 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#5a8ba5] transition-colors"
            >
              Nous contacter
            </Link>
            <a
              href="tel:+41223000262"
              className="flex items-center gap-2 px-10 py-4 text-[11px] uppercase tracking-[0.15em] font-medium text-[#333333] border border-[#333333] hover:bg-[#333333] hover:text-white transition-all"
            >
              <Phone className="w-3.5 h-3.5" />
              Appeler
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
